import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import type {
  UserPrivateProfile,
  UserPublicProfile,
} from "@/pages/profile/api";
import { type AchievementView, achievementViews } from "@/pages/profile/achievements";
import { useI18n } from "@/shared/i18n/LanguageContext";

type ProfileAchievementsProps = {
  profile: UserPublicProfile | UserPrivateProfile;
};

type AchievementPanel = "unlocked" | "locked";

const achievementStatusLabel = (
  achievement: AchievementView,
  labels: {
    statusUnlocked: string;
    statusInProgress: string;
    statusLocked: string;
  },
) => {
  if (achievement.status === "unlocked") {
    return labels.statusUnlocked;
  }

  if (achievement.status === "in_progress") {
    return labels.statusInProgress;
  }

  return labels.statusLocked;
};

export function ProfileAchievements({ profile }: ProfileAchievementsProps) {
  const { copy, language } = useI18n();
  const labels = copy.profile.achievements;
  const achievements = achievementViews(profile, language);
  const unlockedAchievements = achievements.filter((achievement) => achievement.status === "unlocked");
  const lockedAchievements = achievements.filter((achievement) => achievement.status !== "unlocked");
  const [isExpanded, setIsExpanded] = useState(false);
  const [activePanel, setActivePanel] = useState<AchievementPanel>("unlocked");
  const [selectedAchievement, setSelectedAchievement] = useState<AchievementView | null>(null);
  const visibleAchievements = activePanel === "unlocked" ? unlockedAchievements : lockedAchievements;

  useEffect(() => {
    if (!selectedAchievement || typeof document === "undefined") {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelectedAchievement(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedAchievement]);

  const selectPanel = (panel: AchievementPanel) => {
    setActivePanel(panel);
    setSelectedAchievement(null);
  };

  const renderAchievementDetail = (achievement: AchievementView) => (
    <div className="achievement-detail" role="region" aria-label={`${achievement.title} ${labels.details}`}>
      <div className="achievement-detail__crest" data-category={achievement.category}>
        {achievement.mark}
      </div>
      <div className="achievement-detail__copy">
        <span>{achievementStatusLabel(achievement, labels)}</span>
        <h3>{achievement.title}</h3>
        <p>{achievement.subtitle}</p>
      </div>
      <div className="achievement-detail__meter">
        <strong>
          {Math.min(achievement.current, achievement.target)} / {achievement.target}
        </strong>
        <div className="achievement-badge__track">
          <span style={{ width: `${achievement.progressPercent}%` }} />
        </div>
      </div>
      <button
        className="achievement-detail__close"
        type="button"
        aria-label={labels.detailClose}
        onClick={() => setSelectedAchievement(null)}
      >
        x
      </button>
    </div>
  );

  return (
    <article className="profile-section profile-achievements-section">
      <div className="profile-section__head">
        <h2>{labels.title}</h2>
        <button
          className="profile-section__toggle"
          type="button"
          aria-expanded={isExpanded}
          onClick={() => {
            setIsExpanded((current) => !current);
            setSelectedAchievement(null);
          }}
        >
          <span>
            {unlockedAchievements.length} {labels.unlocked}
          </span>
          <strong>{isExpanded ? labels.collapse : labels.expand}</strong>
        </button>
      </div>

      {!isExpanded ? (
        <button
          className="profile-achievements-collapsed"
          type="button"
          onClick={() => setIsExpanded(true)}
        >
          <span>{labels.unlocked}</span>
          <strong>{unlockedAchievements.length}</strong>
          <span>{labels.nextTargets}</span>
          <strong>{lockedAchievements.length}</strong>
        </button>
      ) : null}

      {isExpanded ? (
        <>
      <div className="profile-achievements-tabs" aria-label={labels.filters}>
        <button
          type="button"
          data-active={activePanel === "unlocked" ? "true" : "false"}
          aria-pressed={activePanel === "unlocked"}
          onClick={() => selectPanel("unlocked")}
        >
          <span>{labels.unlocked}</span>
          <strong>{unlockedAchievements.length}</strong>
        </button>
        <button
          type="button"
          data-active={activePanel === "locked" ? "true" : "false"}
          aria-pressed={activePanel === "locked"}
          onClick={() => selectPanel("locked")}
        >
          <span>{labels.locked}</span>
          <strong>{lockedAchievements.length}</strong>
        </button>
      </div>

      {visibleAchievements.length > 0 ? (
        <div className="profile-achievements" data-panel={activePanel} role="list">
          {visibleAchievements.map((achievement) => (
            <div role="listitem" key={achievement.id}>
              <button
                className={`achievement-badge achievement-badge--${achievement.status}`}
                data-category={achievement.category}
                data-selected={selectedAchievement?.id === achievement.id ? "true" : "false"}
                type="button"
                aria-expanded={selectedAchievement?.id === achievement.id}
                onClick={() => setSelectedAchievement(achievement)}
              >
                <span className="achievement-badge__mark">{achievement.mark}</span>
                <div className="achievement-badge__copy">
                  <strong>{achievement.title}</strong>
                </div>
                <div
                  className="achievement-badge__progress"
                  aria-label={`${achievement.current} ${labels.progressOf} ${achievement.target}`}
                >
                  <div className="achievement-badge__track">
                    <span style={{ width: `${achievement.progressPercent}%` }} />
                  </div>
                  <small>
                    {achievement.status === "in_progress"
                      ? `${Math.min(achievement.current, achievement.target)} / ${achievement.target}`
                      : achievementStatusLabel(achievement, labels)}
                  </small>
                </div>
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="profile-achievements-empty" role="status">
          {labels.empty}
        </div>
      )}
        </>
      ) : null}
      {selectedAchievement && typeof document !== "undefined"
        ? createPortal(renderAchievementDetail(selectedAchievement), document.body)
        : null}
    </article>
  );
}
